import { useState } from 'react';
import { T, useGT } from 'gt-react';
import type { ItemListDTO } from '@erp/rpc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/context/ToastContext';
import { useUpdateItemList } from '@/api/rpc/hooks/inventory-management/useItemLists';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  list: ItemListDTO;
}

/** Rename an item list or edit its description. Org-shared, so the change is
 * visible to everyone. */
export function EditItemListDialog(props: Props) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {/* Mount the form only while open so its fields reset on every open. */}
        {props.open && <EditItemListForm {...props} />}
      </DialogContent>
    </Dialog>
  );
}

function EditItemListForm({ onOpenChange, list }: Props) {
  const gt = useGT();
  const { showToast } = useToast();
  const updateList = useUpdateItemList();
  const [name, setName] = useState(list.name);
  const [description, setDescription] = useState(list.description ?? '');

  const canSubmit = name.trim().length > 0 && !updateList.isPending;

  const submit = () => {
    updateList.mutate(
      {
        id: list.id,
        name: name.trim(),
        // An emptied description clears the field (null), not "unchanged".
        description: description.trim() || null,
      },
      {
        onSuccess: () => {
          showToast({
            title: gt('Item list updated', {
              context: 'toast title after renaming an item list',
            }),
            message: gt('Your changes have been saved.', {
              context: 'toast message after renaming an item list',
            }),
            color: 'success',
          });
          onOpenChange(false);
        },
        onError: (error) =>
          showToast({
            title: gt('Error', {
              context: 'update item list error toast title',
            }),
            message:
              error.message ||
              gt('Failed to update the item list.', {
                context: 'fallback error when updating an item list fails',
              }),
            color: 'error',
          }),
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          <T context="edit item list dialog title">Rename item list</T>
        </DialogTitle>
        <DialogDescription>
          <T context="edit item list dialog subtitle">
            The list is shared with your organization — everyone sees the new
            name.
          </T>
        </DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4 py-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-item-list-name">
            <T context="label for the item list name field">Name</T>
          </Label>
          <Input
            id="edit-item-list-name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={updateList.isPending}
            maxLength={255}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="edit-item-list-description">
            <T context="label for the item list description field">
              Description
            </T>
          </Label>
          <Textarea
            id="edit-item-list-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={gt('Optional notes about this list', {
              context: 'placeholder for the item list description field',
            })}
            disabled={updateList.isPending}
            rows={3}
            maxLength={2000}
          />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => onOpenChange(false)}
          disabled={updateList.isPending}
        >
          <T context="cancel button in the edit item list dialog">Cancel</T>
        </Button>
        <Button onClick={() => canSubmit && submit()} disabled={!canSubmit}>
          {updateList.isPending ? (
            <T context="submit button label while the item list update saves">
              Saving…
            </T>
          ) : (
            <T context="submit button label in the edit item list dialog">
              Save
            </T>
          )}
        </Button>
      </DialogFooter>
    </>
  );
}
